import { User } from "./User";
import { Scene, MeshBuilder, BoundingBox, AbstractMesh, DynamicTexture, Mesh, Vector3, StandardMaterial, TransformNode, VideoTexture, Vector4, Color3, Engine, Vector2 } from "@babylonjs/core";
import { MeshAudioDetails } from "./AudioConfig";
import { getDistance, calculateAngleInfoFromSourceToTarget } from "./mathUtils";

export const calculateMeshAudioDetailsGivenMeshes = (source: AbstractMesh, target: AbstractMesh): MeshAudioDetails => {
  const result: MeshAudioDetails = calculateMeshAudioDetailsGivenVectors(source.position, source.rotation, target.position, target.rotation);
  return result;
}

export const calculateMeshAudioDetailsGivenVectors = (sourcePosition: Vector3, sourceRotation: Vector3, targetPosition: Vector3, targetRotation: Vector3): MeshAudioDetails => {
  const distance = getDistance(sourcePosition, targetPosition);
  const sourceToTarget = calculateAngleInfoFromSourceToTarget(sourcePosition, sourceRotation, targetPosition);
  const targetToSource = calculateAngleInfoFromSourceToTarget(targetPosition, targetRotation, sourcePosition);

  const result: MeshAudioDetails = {
    distance,
    sourceToTarget,
    targetToSource
  };

  return result;
}

export const createMaterialForUserMesh = (scene: Scene) => {
  const material = new StandardMaterial('userMaterial', scene);
  material.diffuseColor = new Color3(238/255, 238/255, 238/255);
  return material;
}

export const createTv = (scene: Scene): Mesh => {
  // https://forum.babylonjs.com/t/rounded-corner-for-a-playing-card/630/7
  const scaleAr = 0.9;

  const width = scaleAr;
  const height = scaleAr;
  const depth = 0.001;

  const radius = width * 1.5 / 10; // corner radius;
  const deltaAngle = Math.PI / 16;

  // Polygon shape in XoZ plane held in array
  const shape = [];

  // bottom edge
  shape.push(new Vector3(-width / 2 + radius, 0, -height / 2));
  shape.push(new Vector3(width / 2 - radius, 0, -height / 2));
  // bottom right corner
  let angle;
  for (angle = -Math.PI / 2 + deltaAngle; angle < 0; angle += deltaAngle) {
    shape.push(new Vector3(width / 2 - radius + radius * Math.cos(angle), 0, -height / 2 + radius + radius * Math.sin(angle)));
  }
  // right edge;
  shape.push(new Vector3(width / 2, 0, -height / 2 + radius));
  shape.push(new Vector3(width / 2, 0, height / 2 - radius));
  // top right corner
  for (angle = deltaAngle; angle < Math.PI; angle += deltaAngle) {
    shape.push(new Vector3(width / 2 - radius + radius * Math.cos(angle), 0, height / 2 - radius + radius * Math.sin(angle)));
  }
  // top edge
  shape.push(new Vector3(width / 2 - radius, 0, height / 2));
  shape.push(new Vector3(-width / 2 + radius, 0, height / 2));
  // top left corner
  for (angle = Math.PI / 2 + deltaAngle; angle < Math.PI; angle += deltaAngle) {
    shape.push(new Vector3(-width / 2 + radius + radius * Math.cos(angle), 0, height / 2 - radius + radius * Math.sin(angle)));
  }
  // left edge;
  shape.push(new Vector3(-width / 2, 0, height / 2 - radius));
  shape.push(new Vector3(-width / 2, 0, -height / 2 + radius));
  // bottom left corner
  for (angle = Math.PI + deltaAngle; angle < 3 * Math.PI / 2; angle += deltaAngle) {
    shape.push(new Vector3(-width / 2 + radius + radius * Math.cos(angle), 0, -height / 2 + radius + radius * Math.sin(angle)));
  }

  const faceUV = [];
  faceUV[0] = new Vector4(0, 0, 1, 1);
  faceUV[1] = new Vector4(460 / 469, 0, 1, 1);
  faceUV[2] = new Vector4(445 / 469, 0, 1, 1);
  const tv: Mesh = MeshBuilder.ExtrudePolygon('tvPolygon', { shape, depth, faceUV, sideOrientation: Mesh.DOUBLESIDE }, scene);
  tv.visibility = 0;
  return tv;
}
